/**
 * Shop and stylist photos come from Cloudinary at whatever size they were uploaded, often 3000px wide, to be drawn
 * in a card a few hundred pixels across. Cloudinary resizes on request, so the site asks for the width the picture
 * is really drawn at (twice that, for sharp phone screens), rounded up to a few common sizes so photos are cached
 * and shared, and lets Cloudinary pick the format and quality.
 */
import { IMAGE_WIDTHS, proxiedImagePath } from '../../lib/image-proxy';

const UPLOAD = /^(https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\/)(.*)$/;
const SIZES: readonly number[] = IMAGE_WIDTHS;
// Our own Cloudinary account: its photos are fetched through salunbd.com/img (app/img/[...path]/route.ts), not from Cloudinary.
const IMAGE_CLOUD = process.env.EXPO_PUBLIC_IMAGE_CLOUD;

import { useCallback, useState } from 'react';

/** The direct Cloudinary address behind a salunbd.com/img/... address, or null for any other address. Used when the proxy fails. */
export function directFromProxy(uri: unknown): string | null {
  if (typeof uri !== 'string' || !IMAGE_CLOUD || !uri.startsWith('/img/')) return null;
  return `https://res.cloudinary.com/${IMAGE_CLOUD}/image/upload/${uri.slice('/img/'.length)}`;
}

type Style = unknown;

/** Merges a React Native style (an object, or arrays of them) into one object. */
function flatten(style: Style): Record<string, unknown> {
  if (!style) return {};
  if (Array.isArray(style)) return style.reduce<Record<string, unknown>>((all, item) => ({ ...all, ...flatten(item) }), {});
  return typeof style === 'object' ? (style as Record<string, unknown>) : {};
}

/** How wide the picture is drawn, in CSS pixels, as far as its style says. Unknown sizes count as a large photo. */
function drawnWidth(style: Style): number {
  const { width, height } = flatten(style);
  if (typeof width === 'number') return width;
  // A percentage width fills its card; cards here are about twice as wide as the image is tall.
  if (typeof height === 'number') return height * 2;
  return 450;
}

/** True when the style says how big the picture is drawn. */
function hasSize(style: Style): boolean {
  const { width, height } = flatten(style);
  return typeof width === 'number' || typeof height === 'number';
}

export function optimizeImageUri(uri: unknown, style?: Style): unknown {
  if (typeof uri !== 'string') return uri;
  const match = UPLOAD.exec(uri);
  if (!match) return uri;
  const [, base, rest] = match;
  // Already sized by whoever built the URL: leave it alone.
  if (/(^|[,/])(w|h|c)_[a-z0-9]+/.test(rest.split('/v')[0] ?? '')) return uri;
  const wanted = Math.ceil(drawnWidth(style) * 2);
  const width = SIZES.find((size) => size >= wanted) ?? SIZES[SIZES.length - 1];
  // The path is either "f_auto,q_auto/v123/..." (a transformation first) or "v123/..." (none).
  const hasTransform = !/^v\d+\//.test(rest);
  if (hasTransform) {
    const [transform, ...path] = rest.split('/');
    const sized = `${base}${transform},c_limit,w_${width}/${path.join('/')}`;
    return proxiedImagePath(sized, IMAGE_CLOUD) ?? sized;
  }
  const sized = `${base}f_auto,q_auto,c_limit,w_${width}/${rest}`;
  return proxiedImagePath(sized, IMAGE_CLOUD) ?? sized;
}

/**
 * For a picture that fills whatever box it sits in (absoluteFill, a percentage width), the style cannot say how big
 * it is drawn. It starts at card size (about 180 CSS pixels), which is what nearly all of them are, and is asked for
 * again at a larger size only if, once laid out, it turns out to be drawn wider. A banner is fetched twice; the cards
 * that make up most of the photos are fetched once, at a fraction of the size.
 */
export function useCloudinarySource(source: any, style: Style, onLayout?: (event: any) => void) {
  const [measured, setMeasured] = useState(0);
  const handleLayout = useCallback(
    (event: any) => {
      const width = Math.round(event?.nativeEvent?.layout?.width ?? 0);
      setMeasured((previous) => (width > previous ? width : previous));
      onLayout?.(event);
    },
    [onLayout],
  );
  if (!source || typeof source !== 'object' || typeof source.uri !== 'string') return { source, onLayout };
  const fixedSize = hasSize(style);
  const hint = fixedSize ? style : { width: Math.max(180, measured) };
  return { source: { ...source, uri: optimizeImageUri(source.uri, hint) }, onLayout: fixedSize ? onLayout : handleLayout };
}
